import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useBookingDetails, useSelectBookingOption } from '@/api/queries/booking';
import { centsToUsdString } from '@/utils/money';
import { useLocale } from '@/hooks/useLocale';
import { formatDateTime, formatDurationCompact } from '@/utils/date';
import { paths } from '@/routes/paths';
import styles from './styles.module.css';

export function BookingOptionsPage(): JSX.Element {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { id = '' } = useParams<{ id: string }>();
  const booking = useBookingDetails(id);
  const navigate = useNavigate();
  const select = useSelectBookingOption(id);

  const options = booking.data?.options ?? [];

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('booking.options_title')} />
        {booking.isLoading && <Spinner />}
        {!booking.isLoading && (
          <p style={{ color: 'var(--color-text-on-dark-muted)' }}>
            {t('booking.options_subtitle', { count: options.length })}
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {options.map((o) => (
            <Card key={o.id} variant="light" padding="md">
              <div className={styles.route}>
                <span className={styles.iata}>{o.from_iata}</span>
                <span className={styles.airArrow}>→</span>
                <span className={styles.iata}>{o.to_iata}</span>
              </div>
              <div className={styles.meta}>
                <span>{o.carrier_name} {o.flight_number}</span>
                <span>·</span>
                <span>{formatDurationCompact(o.duration_minutes, locale)}</span>
                <span>·</span>
                <span>{t(`booking.class.${o.class}`)}</span>
              </div>
              <div className={styles.time}>
                {formatDateTime(o.depart_at, locale)} — {formatDateTime(o.arrive_at, locale)}
              </div>
              <div className={styles.priceRow}>
                <span className={styles.price}>{centsToUsdString(o.price_cents, locale)}</span>
                <Button
                  size="sm"
                  variant="inverted"
                  onClick={async () => {
                    await select.mutateAsync(o.id);
                    navigate(paths.boardingPass(id));
                  }}
                  loading={select.isPending}
                >
                  {t('booking.choose')}
                </Button>
              </div>
            </Card>
          ))}
          {!booking.isLoading && options.length === 0 && (
            <div style={{ color: 'var(--color-text-on-dark-muted)' }}>{t('booking.no_options')}</div>
          )}
        </div>
      </PageContainer>
    </AppShell>
  );
}
